import { Container, Title, Text, Paper, Box, Group, useMantineTheme, Stack, Button, SimpleGrid, ThemeIcon } from '@mantine/core';
import { IconMail, IconPhone, IconMapPin, IconClock, IconParking, IconDirections } from '@tabler/icons-react';
import { ClinicMap } from '../components/ClinicMap';
import { ContactForm } from '../components/ContactForm';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

export function ContactPage() {
  const theme = useMantineTheme();
  
  return (
    <>
      <SEOHead 
        title="Kontakt Lykke Terapien"
        description="Kontakt Lykke Terapien i Horsens for spørgsmål eller booking. Jeg står klar til at hjælpe dig med akupunktur og velvære."
        canonical="/kontakt"
      />
      <Container size="md" py="xl" pt={50} mx="auto" className="contact-page-container">
      {/* Main Header */}
      <Box ta="center" mb="xl" className="contact-header">
        <Title order={1} c={theme.colors.gold[7]} mb="md" className="contact-main-title">
          Kontakt mig
        </Title>
        <Text size="lg" c="gray.6" maw={600} mx="auto" lh={1.6} className="contact-subtitle">
          Jeg er her for at hjælpe dig på din vej til bedre velvære. Kontakt mig i dag for at booke din første konsultation.
        </Text>
      </Box>

      {/* Contact Information Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg" mb="xl" className="contact-cards-grid">
        {/* Phone */}
        <Paper 
          p="lg" 
          radius="md" 
          withBorder 
          shadow="sm"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.gold[0]} 0%, ${theme.colors.gold[1]} 100%)`,
            borderColor: theme.colors.gold[2],
            textAlign: 'center'
          }}
          className="contact-card"
        >
          <ThemeIcon size="xl" radius="50%" color="gold" variant="light" mb="md" mx="auto">
            <IconPhone size={24} />
          </ThemeIcon>
          <Text fw={600} size="md" mb="xs" c={theme.colors.dark[7]}>
            Ring til mig
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            Mandag-Fredag 9:00-17:00
          </Text>
          <Button
            component="a"
            href="tel:+4560803735"
            variant="light"
            color="gold"
            size="sm"
            fullWidth
          >
            +45 60 80 37 35
          </Button>
        </Paper>

        {/* Email */}
        <Paper 
          p="lg" 
          radius="md" 
          withBorder 
          shadow="sm"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.blue[0]} 0%, ${theme.colors.blue[1]} 100%)`,
            borderColor: theme.colors.blue[2],
            textAlign: 'center'
          }}
          className="contact-card"
        >
          <ThemeIcon size="xl" radius="50%" color="blue" variant="light" mb="md" mx="auto">
            <IconMail size={24} />
          </ThemeIcon>
          <Text fw={600} size="md" mb="xs" c={theme.colors.dark[7]}>
            Send en email
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            Jeg svarer inden for 24 timer
          </Text>
          <Button
            component="a"
            href="mailto:lykketerapien@gmail.com"
            variant="light"
            color="blue"
            size="sm"
            fullWidth
          >
            lykketerapien@gmail.com
          </Button>
        </Paper>

        {/* Book Online */}
        <Paper 
          p="lg" 
          radius="md" 
          withBorder 
          shadow="sm"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.green[0]} 0%, ${theme.colors.green[1]} 100%)`,
            borderColor: theme.colors.green[2],
            textAlign: 'center'
          }}
          className="contact-card"
        >
          <ThemeIcon size="xl" radius="50%" color="green" variant="light" mb="md" mx="auto">
            <IconClock size={24} />
          </ThemeIcon>
          <Text fw={600} size="md" mb="xs" c={theme.colors.dark[7]}>
            Book online
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            Hurtig og nem booking
          </Text>
          <Button
            component="a"
            href="https://lykke-terapien.planway.com/"
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
            color="green"
            size="sm"
            fullWidth
          >
            Book tid nu
          </Button>
        </Paper>
      </SimpleGrid>

      {/* Contact Form Section */}
      <Box mb="xl">
        <ContactForm />
      </Box>

      {/* Main Contact Paper */}
      <Paper
        p="xl"
        radius="md"
        withBorder
        shadow="sm"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          borderColor: theme.colors.gold[2]
        }}
        className="contact-main-paper"
        mb="xl"
      >
        <Group mb="lg" align="center" className="contact-info-header">
          <IconMapPin size={24} style={{ color: theme.colors.gold[6] }} />
          <Title order={2} c={theme.colors.gold[7]} className="contact-info-title">
            Besøg klinikken
          </Title>
        </Group>
        
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" className="contact-info-grid">
          {/* Left Column - Details */}
          <Box>
            <Stack gap="md" className="contact-details">
              <Box>
                <Text fw={600} size="lg" mb="xs" c={theme.colors.dark[7]}>
                  Lykke Terapien
                </Text>
                <Text c="dimmed" size="sm">
                  V/ Pernille Lykke Nielsen
                </Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <IconMapPin size={16} color={theme.colors.gold[6]} />
                  <Text fw={500} size="md">Adresse</Text>
                </Group>
                <Text>Stampemøllevej 21</Text>
                <Text>8700 Horsens</Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <IconClock size={16} color={theme.colors.gold[6]} />
                  <Text fw={500} size="md">Åbningstider</Text>
                </Group>
                <Text size="sm">Mandag-Fredag: 7:00-17:00</Text>
                <Text size="sm">Weekend: Efter aftale</Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <IconParking size={16} color={theme.colors.gold[6]} />
                  <Text fw={500} size="md">Parkering</Text>
                </Group>
                <Text size="sm">
                  Gratis parkering lige uden for klinikken
                </Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <IconDirections size={16} color={theme.colors.gold[6]} />
                  <Text fw={500} size="md">Vejvisning</Text>
                </Group>
                <Text size="sm">
                  Klinikken ligger i rolige omgivelser nær Horsens. 
                  Du finder mig på Stampemøllevej 21 med let adgang og parkering.
                </Text>
              </Box>
            </Stack>
          </Box>

          {/* Right Column - Additional Info */}
          <Box>
            <Paper 
              p="md" 
              radius="md" 
              style={{
                background: 'rgba(220, 248, 255, 0.3)',
                borderColor: theme.colors.blue[3]
              }}
              withBorder
              className="contact-info-paper"
            >
              <Text fw={600} size="md" mb="md" c={theme.colors.blue[7]}>
                Andet
              </Text>
              <Stack gap="sm">
                <Text size="sm" lh={1.5}>
                  <strong>CVR:</strong> 45461378
                </Text>
                <Text size="sm" lh={1.5}>
                  <strong>Aflysning:</strong> Minimum 24 timer før
                </Text>
                <Text size="sm" lh={1.5}>
                  <strong>Betaling:</strong> Kontant eller MobilePay
                </Text>
              </Stack>
            </Paper>
          </Box>
        </SimpleGrid>
      </Paper>

      {/* Map Section */}
      <Box className="contact-map-container">
        <ClinicMap />
      </Box>

      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .contact-page-container {
            padding: 1rem !important;
            padding-top: 60px !important;
          }
          
          .contact-header {
            margin-bottom: 2rem !important;
          }
          
          .contact-main-title {
            font-size: 2rem !important;
            margin-bottom: 1rem !important;
          }
          
          .contact-subtitle {
            font-size: 1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          .contact-cards-grid {
            gap: 1rem !important;
            margin-bottom: 2rem !important;
          }
          
          .contact-card {
            padding: 1rem !important;
            min-height: auto !important;
            text-align: center !important;
          }
          
          .contact-card .mantine-ThemeIcon-root {
            width: 50px !important;
            height: 50px !important;
            margin-bottom: 0.75rem !important;
          }
          
          .contact-card .mantine-Text-root[data-fw="600"] {
            font-size: 1rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .contact-card .mantine-Text-root[data-c="dimmed"] {
            font-size: 0.875rem !important;
            margin-bottom: 0.75rem !important;
          }
          
          .contact-card .mantine-Button-root {
            font-size: 0.875rem !important;
            height: 40px !important;
            padding: 0.5rem 0.75rem !important;
          }
          
          .contact-main-paper {
            padding: 1.5rem !important;
            margin-bottom: 2rem !important;
          }
          
          .contact-info-header {
            margin-bottom: 1.5rem !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          .contact-info-title {
            font-size: 1.5rem !important;
          }
          
          .contact-info-grid {
            gap: 1.5rem !important;
          }
          
          .contact-details {
            gap: 1.5rem !important;
          }
          
          .contact-info-paper {
            padding: 1rem !important;
          }
          
          .contact-info-paper .mantine-Text-root {
            font-size: 0.875rem !important;
          }
          
          .contact-map-container {
            margin-top: 2rem !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .contact-page-container {
            padding: 1.5rem !important;
            padding-top: 70px !important;
          }
          
          .contact-main-title {
            font-size: 2.5rem !important;
          }
          
          .contact-subtitle {
            font-size: 1.125rem !important;
          }
          
          .contact-main-paper {
            padding: 2rem !important;
          }
          
          .contact-card {
            padding: 2rem !important;
          }
        }
        
        /* Enhanced interactions */
        .contact-card {
          transition: all 0.3s ease !important;
          cursor: pointer;
        }
        
        .contact-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
        
        .contact-card .mantine-Button-root {
          transition: all 0.2s ease;
        }
        
        .contact-card .mantine-Button-root:hover {
          transform: scale(1.05);
        }
        
        /* Better spacing for mobile */
        @media (max-width: 768px) {
          .contact-details .mantine-Box-root {
            padding: 0.5rem 0;
            border-bottom: 1px solid ${theme.colors.gray[2]};
          }
          
          .contact-details .mantine-Box-root:last-child {
            border-bottom: none;
            padding-bottom: 0;
          }
        }
        
        /* Enhanced focus states */
        @media (max-width: 768px) {
          .contact-card:focus,
          .mantine-Button-root:focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
        }
        
        /* Improved grid layout */
        @media (max-width: 768px) {
          .contact-cards-grid .mantine-SimpleGrid-root {
            grid-template-columns: 1fr !important;
          }
          
          .contact-info-grid .mantine-SimpleGrid-root {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* Better visual hierarchy */
        @media (max-width: 768px) {
          .contact-info-header .mantine-Group-root {
            flex-direction: column !important;
            gap: 0.5rem !important;
          }
        }
      `}</style>
      </Container>
    </>
  );
}